<template>
    <div class="sec-cont">
        <Head :title="title"/>
        <div class="max-w-full bg-white rounded-md shadow overflow-hidden p-6">
            <form @submit.prevent="submitForm">

                <!-- Applicant Details Section -->
                <h2 class="text-xl font-bold mt-6 mb-4">Staff Details</h2>

                <div class="flex flex-wrap -mb-8 -mr-6" v-if="permissionSlugs.includes('edit_interview_admin_section')">
                    <!-- Training Radio Button -->
                    <div class="flex flex-wrap w-full">
                        <div class="mb-8 pr-6 w-full lg:w-1/2">
                            <label class="form-label">{{ __('Training Required') }}</label>
                            <div class="mt-2 flex items-center">
                                <div class="mr-6">
                                    <input type="radio" id="yes" value="yes" v-model="form.training_required"
                                           class="mr-2"
                                    />
                                    {{ __('Yes') }}
                                </div>
                                <div>
                                    <input type="radio" id="no" value="no" v-model="form.training_required"
                                           class="mr-2"
                                    />
                                    {{ __('No') }}
                                </div>
                            </div>
                        </div>

                        <select-input v-model="form.staff_trainer_id" :error="form.errors?.staff_trainer_id"
                                      v-if="form.training_required === 'yes'"
                                      class="pb-4 pr-6 w-full lg:w-1/2" :label="__('Select Employee')"
                        ><!-- Placeholder option with empty string -->
                            <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                                {{ employee.first_name }} {{ employee.last_name }}
                            </option>
                        </select-input>
                    </div>


                    <!-- Date From-To -->
                    <text-input v-model="form.training_from_date" :error="form.errors?.training_from_date || ''"
                                class="pb-8 pr-6 w-full lg:w-1/3" :label="__('From Date')" type="date"
                                :max="currentDate"
                                @focus.native="$event.target.showPicker()"
                    />
                    <text-input v-model="form.training_to_date" :error="form.errors?.training_to_date"
                                class="pb-8 pr-6 w-full lg:w-1/3" :label="__('To Date')" type="date"
                                :max="currentDate"
                                @focus.native="$event.target.showPicker()"
                    />

                    <!-- Remark by Admin -->
                    <text-input v-model="form.remark_by_admin" :error="form.errors?.remark_by_admin || ''"
                                class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Remark by Admin')"
                    />

                </div>


                <div class="flex flex-wrap -mb-8 -mr-6"
                     v-if="permissionSlugs.includes('edit_interview_trainer_section')"
                >
                    <!-- Training Start Date -->
                    <text-input v-model="form.training_start_date" :error="form.errors?.training_start_date"
                                @focus.native="$event.target.showPicker()"
                                class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Training Start Date')" type="date"
                    />

                    <!-- Completion Checkbox with Date -->
                    <div class="pb-8 pr-6 w-full lg:w-1/3">
                        <label class="form-label">{{ __('Complete') }}</label>
                        <div class="mt-2 flex items-center">
                            <div class="mr-6">
                                <input type="radio" id="complete_yes" value="yes" v-model="form.training_complete"
                                       class="mr-2"
                                />
                                {{ __('Yes') }}
                            </div>
                            <div>
                                <input type="radio" id="complete_no" value="no" v-model="form.training_complete"
                                       class="mr-2"
                                />
                                {{ __('No') }}
                            </div>
                        </div>
                    </div>


                    <text-input v-model="form.training_completion_date" :error="form.errors?.training_completion_date"
                                class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Completion Date')" type="date"
                                :max="currentDate"
                                @focus.native="$event.target.showPicker()"
                    />


                    <!-- Positive Points -->
                    <textarea-input v-model="form.positive_points_by_trainer"
                                    :error="form.errors?.positive_points_by_trainer"
                                    class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Positive Points')"
                    />

                    <!-- Negative Points -->
                    <textarea-input v-model="form.negative_points_by_trainer"
                                    :error="form.errors?.negative_points_by_trainer"
                                    class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Negative Points')"
                    />

                    <!-- Suggestions -->
                    <textarea-input v-model="form.suggestion_by_trainer" :error="form.errors?.suggestion_by_trainer"
                                    class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Suggestions')"
                    />

                    <!-- Issue Articles -->
                    <text-input v-model="form.issue_articles" :error="form.errors?.issue_articles"
                                class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Issue Articles')"
                    />

                    <!-- Remarks -->
                    <text-input v-model="form.trainer_remarks" :error="form.errors?.trainer_remarks"
                                class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Remarks')"
                    />
                </div>

                <div v-if="permissionSlugs.includes('edit_interview_admin_section')">

                    <div class="flex flex-wrap mt-4 -mr-6">
                        <textarea-input v-model="form.positive_points_by_admin"
                                        :error="form.errors?.positive_points_by_admin"
                                        class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Positive Points By Admin')"
                        />

                        <!-- Negative Points -->
                        <textarea-input v-model="form.negative_points_by_admin"
                                        :error="form.errors?.negative_points_by_admin"
                                        class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Negative Points By Admin')"
                        />

                        <!-- Suggestions -->
                        <textarea-input v-model="form.suggestion_by_admin" :error="form.errors?.suggestion_by_admin"
                                        class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Suggestions By Admin')"
                        />
                    </div>

                    <h2 class="text-xl font-bold mb-4">Employee Details</h2>

                    <div class="flex flex-wrap -mb-8 -mr-6">
                        <div class="pb-8 pr-6 w-full lg:w-1/4">
                            <h3 class="font-bold mb-4">Employee's Photo</h3>
                            <!-- Show existing image if available -->
                            <div v-if="form.employee_photo" class="mt-4">
                                <img :src="form.employee_photo" alt="Employee Photo" class="w-1/2 photo-preview"/>
                            </div>

                            <file-pond
                                type="file"
                                :name="employee_photo"
                                :files="getPreloadedFile('employee')"
                                @change="handlePhotoChange($event, 'employee')"
                                @removefile="removeFile('employee')"
                                class="form-input w-full photo-preview"
                                accept="image/*"
                            />
                            <div v-if="photos.employee" class="mt-4 photo-container">
                                <img :src="photos.employee" alt="Employee Photo" class="w-1/2 photo-preview"/>
                            </div>
                        </div>

                        <!-- Father Photo Upload Section -->
                        <div class="pb-8 pr-6 w-full lg:w-1/4">
                            <h3 class="font-bold mb-4">Father's Photo</h3>
                            <file-pond
                                type="file"
                                name="father_photo"
                                :files="getPreloadedFile('father')"
                                @change="handlePhotoChange($event, 'father')"
                                @removefile="removeFile('father')"
                                class="form-input w-full photo-preview"
                                accept="image/*"
                            />
                            <div v-if="photos.father" class="mt-4 photo-container">
                                <img :src="photos.father" alt="Father's Photo" class="w-1/2 photo-preview"/>
                            </div>
                        </div>

                        <!-- Mother Photo Upload Section -->
                        <div class="pb-8 pr-6 w-full lg:w-1/4">
                            <h3 class="font-bold mb-4">Mother's Photo</h3>
                            <file-pond
                                type="file"
                                name="mother_photo"
                                :files="getPreloadedFile('mother')"
                                @change="handlePhotoChange($event, 'mother')"
                                class="form-input w-full photo-preview"
                                accept="image/*"
                            />
                            <div v-if="photos.mother" class="mt-4 photo-container">
                                <img :src="photos.mother" alt="Mother's Photo" class="w-1/2 photo-preview"/>
                            </div>
                        </div>

                        <!-- Spouse Photo Upload Section -->
                        <div class="pb-8 pr-6 w-full lg:w-1/4">
                            <h3 class="font-bold mb-4">Spouse's Photo</h3>
                            <file-pond
                                type="file"
                                name="spouse_photo"
                                :files="getPreloadedFile('spouse')"
                                @change="handlePhotoChange($event, 'spouse')"
                                class="form-input w-full photo-preview"
                                accept="image/*"
                            />
                            <div v-if="photos.spouse" class="mt-4 photo-container">
                                <img :src="photos.spouse" alt="Spouse's Photo" class="w-1/2 photo-preview"/>
                            </div>
                        </div>


                        <!-- Employee No. -->
                        <text-input v-model="form.employee_no" :error="form.errors?.employee_no"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Employee No.')"
                        />

                        <!-- User ID -->
                        <text-input v-model="form.employee_user_id" :error="form.errors?.employee_user_id"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('User ID')"
                        />


                        <text-input v-model="form.joining_date" :error="form.errors?.joining_date"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Joining Date')" type="date"
                        />

                        <!-- Status -->
                        <select-input v-model="form.application_status" :error="form.errors?.application_status"
                                      class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Status')"
                        >
                            <option v-for="(label, code) in application_status_codes" :value="code" :key="code">
                                {{ label }}
                            </option>
                        </select-input>

                        <!-- Salutation -->
                        <select-input v-model="form.salutation" :error="form.errors?.salutation"
                                      class="pb-8 pr-6 w-full lg:w-1/6" :label="__('Salutation')"
                        >
                            <option value="Mr.">{{ __('Mr.') }}</option>
                            <option value="Ms.">{{ __('Ms.') }}</option>
                            <option value="Mrs.">{{ __('Mrs.') }}</option>
                        </select-input>

                        <!-- Name -->
                        <text-input v-model="form.name" :error="form.errors?.name"
                                    class="pb-8 pr-6 w-full lg:w-1/3" :label="__('Name')"
                        />
                    </div>


                    <h2 class="text-xl font-bold mt-6 mb-4">Employment Details</h2>

                    <div class="flex flex-wrap -mb-8 -mr-6">

                        <!-- Employment Type -->
                        <select-input v-model="form.employment_type" :error="form.errors?.employment_type"
                                      class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Employment Type')"
                        >
                            <option value="Probation">{{ __('Probation') }}</option>
                            <option value="Regular">{{ __('Regular') }}</option>
                            <option value="Contract Full Time">{{ __('Contract Full Time') }}</option>
                            <option value="Contract Part Time">{{ __('Contract Part Time') }}</option>
                            <option value="Placement Agency">{{ __('Placement Agency') }}</option>
                        </select-input>

                        <text-input v-model="form.designation" :error="form.errors.designation"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Designation')"
                        />
                        <text-input v-model="form.responsibility" :error="form.errors.responsibility"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Responsibility')"
                        />
                        <text-input v-model="form.duty" :error="form.errors.duty"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Duty')"
                        />


                    </div>
                    <h2 class="text-xl font-bold mt-6 mb-4">Family Details</h2>

                    <div class="flex flex-wrap -mb-8 -mr-6">
                        <!-- Father Name -->
                        <text-input v-model="form.father_name" :error="form.errors?.father_name"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Father Name')"
                        />

                        <!-- Mother Name -->
                        <text-input v-model="form.mother_name" :error="form.errors?.mother_name"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Mother Name')"
                        />

                        <!-- Date of Birth -->
                        <text-input v-model="form.date_of_birth" :error="form.errors?.date_of_birth"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Date of Birth')" type="date"
                        />

                        <div class="flex flex-wrap w-full">
                            <!-- Gender Radio Buttons -->
                            <div class="pb-8 pr-6 lg:w-1/2">
                                <label class="form-label">{{ __('Gender') }}</label>
                                <div>
                                    <input type="radio" id="male" value="male" v-model="form.gender"/> Male
                                    <input type="radio" id="female" value="female" v-model="form.gender" class="ml-4"/>
                                    Female
                                </div>
                            </div>

                            <!-- Marital Status Radio Buttons -->
                            <div class="pb-8 pr-6 w-full lg:w-1/2">
                                <label class="form-label">{{ __('Marital Status') }}</label>
                                <div>
                                    <input type="radio" id="married" value="married" v-model="form.marital_status"/>
                                    Married
                                    <input type="radio" id="unmarried" value="unmarried" v-model="form.marital_status"
                                           class="ml-4"
                                    /> Unmarried
                                </div>
                            </div>
                        </div>

                        <!-- Conditional Spouse Name and Occupation -->
                        <div v-if="form.marital_status === 'married'" class="pb-8 pr-6 w-full lg:w-1/2">
                            <text-input v-model="form.spouse_name" :error="form.errors?.spouse_name"
                                        class="w-full lg:w-full" :label="__('Spouse Name')"
                            />
                        </div>
                        <div v-if="form.marital_status === 'married'" class="pb-8 pr-6 w-full lg:w-1/2">
                            <text-input v-model="form.spouse_occupation" :error="form.errors?.spouse_occupation"
                                        class="w-full lg:w-full" :label="__('Spouse Occupation')"
                            />
                        </div>

                        <!-- Contact Information -->
                        <text-input v-model="form.contact_number" :error="form.errors?.contact_number"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Contact Number')" type="number"
                        />

                        <text-input v-model="form.alternative_contact" :error="form.errors?.alternative_contact"
                                    type="number"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Alternative Contact Number')"
                        />
                        <text-input v-model="form.whatsapp_number" :error="form.errors?.whatsapp_number"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('WhatsApp Number')" type="number"
                        />
                        <text-input v-model="form.home_contact_number" :error="form.errors?.home_contact_number"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Home Contact Number')"
                                    placeholder="e.g., 1234-567-890"
                        />

                        <!-- Email -->
                        <text-input v-model="form.email" :error="form.errors?.email"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Email')" type="email"
                        />

                        <!-- Aadhaar Number -->
                        <text-input v-model="form.aadhaar_number" :error="form.errors?.aadhaar_number"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Aadhaar Number')" type="number"
                        />

                        <!-- Address -->
                        <textarea-input v-model="form.current_address" :error="form.errors?.current_address"
                                        class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Current Address')"
                        />
                        <textarea-input v-model="form.permanent_address" :error="form.errors?.permanent_address"
                                        class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Permanent Address')"
                        />

                        <!-- Category -->
                        <select-input v-model="form.category" :error="form.errors?.category"
                                      class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Category')"
                        >
                            <option value="gen">{{ __('General') }}</option>
                            <option value="obc">{{ __('OBC') }}</option>
                            <option value="sc">{{ __('SC') }}</option>
                            <option value="st">{{ __('ST') }}</option>
                            <option value="other">{{ __('Other') }}</option>
                        </select-input>
                        <text-input v-if="form.category === 'other'" v-model="form.other_category"
                                    :error="form.errors?.other_category"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Please specify Other Category')"
                        />

                        <!-- PAN Number -->
                        <text-input v-model="form.pan_number" :error="form.errors?.pan_number"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('PAN Number')"
                        />

                        <!-- Bank Details -->
                        <text-input v-model="form.bank_name_branch" :error="form.errors?.bank_name_branch"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Bank Name & Branch')"
                        />
                        <text-input v-model="form.bank_account_number" :error="form.errors?.bank_account_number"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Bank Account Number')"
                        />
                        <text-input v-model="form.ifsc_code" :error="form.errors?.ifsc_code"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('IFSC Code')"
                        />

                        <!-- Qualification / Degree -->
                        <text-input v-model="form.qualification" :error="form.errors?.qualification"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Qualification / Degree')"
                        />

                        <!-- Teaching Details -->
                        <text-input v-model="form.teaching_classes" :error="form.errors?.teaching_classes"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Teaching Class(es)')"
                        />
                        <text-input v-model="form.teaching_subjects" :error="form.errors?.teaching_subjects"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Teaching Subject(s)')"
                        />

                        <!-- Total Experience -->
                        <text-input v-model="form.total_experience" :error="form.errors?.total_experience" type="number"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Total Experience')"
                        />

                        <div class="lg:w-full">
                            <keep-alive>
                                <student-info
                                    v-model="form.childrens"
                                    ref="studentInfo"
                                    :errors="form.errors"
                                    :get-preloaded-file="getPreloadedFile"
                                    @removeXFile="removeFile"
                                    @studentChange="handlePhotoChange"
                                />
                            </keep-alive>
                        </div>

                        <!-- Academic Qualifications -->
                        <AcademicQualifications
                            class="lg:w-full"
                            v-model="form.academic_qualifications"
                            ref="academicQualifications"
                            @add-row="addRow"
                            @save-row="saveRow"
                            @delete-row="deleteRow"
                        />

                        <!-- Any Other Skill -->
                        <text-input v-model="form.other_skills" :error="form.errors?.other_skills"
                                    class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Any Other Skill')"
                        />

                        <!-- Holiday List -->
                        <select-input v-model="form.holiday_list" :error="form.errors?.holiday_list"
                                      class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Holiday List')"
                        >
                            <option value="office_staff">{{ __('Office Staff') }}</option>
                            <option value="academic_staff">{{ __('Academic Staff') }}</option>
                            <option value="iv_grade_staff">{{ __('IV Grade Staff') }}</option>
                            <option value="drivers">{{ __('Drivers') }}</option>
                        </select-input>


                        <div class="lg:w-full">
                            <DocumentList
                                v-model="form.otherDocuments"
                                ref="documentList"
                                :errors="form.errors"
                                :get-preloaded-file="getPreloadedFile"
                                @remove-children="removeChildren"
                                @add-children="addChildren"
                                @documentRemoveImage="removeFile"
                                @documentChange="handlePhotoChange"
                            />
                        </div>
                    </div>

                </div>

                <!-- Submit Button -->
                <div class="flex items-center justify-center px-8 py-4 bg-gray-50 border-t border-gray-100 mt-8">
                    <loading-button :loading="form.processing" class="btn-indigo mx-auto" type="submit">{{
                            __('Submit')
                        }}
                    </loading-button>
                </div>


            </form>
        </div>
    </div>
</template>

<script>
import {Head, Link} from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TextareaInput from '@/Shared/TextareaInput'
import FileUploader from '@/Shared/FileUploader'
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'
import StudentInfo from '@/Shared/Component/StudentInfo'
import DocumentList from '@/Shared/Component/DocumentList'
import AcademicQualifications from '@/Pages/Interview/AcademicQualifications'
import {ref} from 'vue'
import axios from 'axios'
import {v4 as uuid} from 'uuid'

// Register the component globally
const FilePond = vueFilePond()

export default {
    components: {
        FileInput,
        Head,
        Link,
        LoadingButton,
        SelectInput,
        TextInput,
        TextareaInput,
        FileUploader,
        FilePond,
        AcademicQualifications,
        StudentInfo,
        DocumentList,
    },
    layout: Layout,

    props: {
        applicant: {
            type: Object,
            required: true, // This ensures that applicant should be provided
        },
        documents: {
            type: Object,
            required: true, // This ensures that applicant should be provided
        },
        employees: {
            type: Array,
            required: true,
        },
        permissions: {
            type: Array,
            required: true,
        },
        application_status_codes:
            {
                type: Array,
                required: true, // This ensures that applicant should be provided
            },
        uuid: {
            type: String,
            default() {
                return `${uuid()}`
            },
        },
        document_type: {
            type: String,
        },
        showRemoveButton: Boolean,
        removed_document: Number,
    },

    data() {
        return {
            removed_document: null,
            shouldLoadFile: true,
            photos: {
                employee: null,
                father: null,
                mother: null,
                spouse: null,
            },
            documentUploads: [
                {
                    selectedFile: null,
                    name: '',
                    uploaded: false,
                    preview: null,
                    fileItem: null,
                },
            ], // Array for document uploads
            // Stores the preview URLs for the employee and family photos
            files: [], // Stores the files selected in FilePond
            selectedFile: null, // Stores the selected file
            documentName: '', // The user-defined name for the document
            uploadedDocuments: [], // Stores the uploaded documents
            fileUploaded: false, // Track if file is uploaded
            form: this.$inertia.form({
                id: this.applicant.id,
                name: this.applicant.name ?? '',
                father_name: this.applicant.father_name || '',
                date_of_birth: this.applicant.date_of_birth || '',
                contact_number: this.applicant.contact_number || '',
                marital_status: this.applicant.marital_status || '',
                schooling_name: this.applicant.schooling_name || '',
                schooling_medium: this.applicant.schooling_medium || '',
                favourite_subjects: this.applicant.favourite_subjects || '',
                teaching_class_limit: this.applicant.teaching_class_limit || '',
                reason_to_join: this.applicant.reason_to_join || '',
                about_academy: this.applicant.about_academy || '',
                prefer_teaching_reason: this.applicant.prefer_teaching_reason || '',
                positive_points: this.applicant.positive_points || '',
                negative_points: this.applicant.negative_points || '',
                positive_points_by_trainer: this.applicant.positive_points_by_trainer || '',
                negative_points_by_trainer: this.applicant.negative_points_by_trainer || '',
                suggestion_by_trainer: this.applicant.suggestion_by_trainer || '',
                positive_points_by_admin: this.applicant.positive_points_by_admin || '',
                negative_points_by_admin: this.applicant.negative_points_by_admin || '',
                suggestion_by_admin: this.applicant.suggestion_by_admin || '',
                previous_work_experience: this.applicant.previous_work_experience || '',
                leave_reason: this.applicant.leave_reason || '',
                salary_expectation: this.applicant.salary_expectation || '',
                continue_duration: this.applicant.continue_duration || '',
                application_date: this.applicant.application_date || '',
                application_status: this.applicant.application_status || '',
                other_qualities: this.applicant.other_qualities || '',
                commitments: this.applicant.commitments || '',
                application_status_codes: this.application_status_codes,
                training_required: this.applicant.training_required || null,
                staff_trainer_id: this.applicant.staff_trainer_id || null,
                training_from_date: this.applicant.training_from_date || null,
                training_to_date: this.applicant.training_to_date || null,
                remark_by_admin: this.applicant.remark_by_admin || null,
                training_start_date: this.applicant.training_start_date || null,
                training_complete: this.applicant.training_complete || null,
                training_completion_date: this.applicant.training_completion_date || null,
                issue_articles: this.applicant.issue_articles || null,
                trainer_remarks: this.applicant.trainer_remarks || null,
                employee_photo: this.applicant.employee_photo || null,
                father_photo: this.applicant.father_photo || null,
                mother_photo: this.applicant.mother_photo || null,
                spouse_photo: this.applicant.spouse_photo || null,
                joining_date: this.applicant.joining_date || null,
                salutation: this.applicant.salutation || '',
                employee_no: this.applicant.employee_no || null,
                employee_user_id: this.applicant.employee_user_id || null,
                employee_status: this.applicant.employee_status || null,
                employment_type: this.applicant.employment_type || null,
                responsibility: this.applicant.responsibility || null,
                designation: this.applicant.designation || null,
                duty: this.applicant.duty || null,
                mother_name: this.applicant.mother_name || null,
                gender: this.applicant.gender || null,
                spouse_name: this.applicant.spouse_name || null,
                spouse_occupation: this.applicant.spouse_occupation || null,
                whatsapp_number: this.applicant.whatsapp_number || null,
                alternative_contact: this.applicant.alternative_contact || null,
                home_contact_number: this.applicant.home_contact_number || null,
                email: this.applicant.email || null,
                aadhaar_number: this.applicant.aadhaar_number || null,
                current_address: this.applicant.current_address || '',
                permanent_address: this.applicant.permanent_address || null,
                category: this.applicant.category || null,
                other_category: this.applicant.other_category || null,
                pan_number: this.applicant.pan_number || null,
                bank_name_branch: this.applicant.bank_name_branch || null,
                bank_account_number: this.applicant.bank_account_number || null,
                ifsc_code: this.applicant.ifsc_code || null,
                teaching_classes: this.applicant.teaching_classes || null,
                teaching_subjects: this.applicant.teaching_subjects || null,
                total_experience: this.applicant.total_experience || null,
                /*student_name: this.applicant.student_name || null,
                student_class: this.applicant.student_class || null,
                school_name: this.applicant.school_name || null,*/
                academic_qualifications: this.applicant.academic_qualifications || [],
                qualification: this.applicant.qualification || [],
                other_skills: this.applicant.other_skills || null,
                holiday_list: this.applicant.holiday_list || null,
                documents: this.applicant.documents || [],
                childrens: this.applicant.children || [{name: '', class: '', school_name: ''}],
                otherDocuments: this.applicant.otherDocuments || [{document_name: ''}],
                employees: this.employees,
                permissions: this.permissions,
                removedFiles: [],
                errors: {},
                processing: false,
            }),
            uploadedFiles: {
                employee_photo: this.applicant.employee || null,
                father_photo: null,
                mother_photo: null,
                spouse_photo: null,
            },

            currentDate: new Date().toISOString().split('T')[0],
        }
    },
    computed: {
        permissionSlugs() {
            return this.permissions.map(permission => permission.slug)
        },
    },
    methods: {
        getPreloadedFile(name) {
            console.log('getPreloadedFile called ' + name)
            console.log(this.form.documents)
            if (!this.form.documents || this.form.documents.length <= 0)
                return

            const document = this.form.documents.find(doc => doc.name === name)
            console.log(document)
            if (document && document.path) {
                const path = document.path
                //    this.removed_document = document.id
                console.log(path)
                return [{source: path}]
            }
        },
        fileAdded(fileItem, index) {
            // FilePond passes the file object directly as part of fileItem.file
            const file = fileItem.target.files[0]

            // Store the selected file for the specific document upload section
            this.documentUploads[index].selectedFile = file
            this.documentUploads[index].fileItem = fileItem

            // Generate a preview for image files or other supported types
            if (file) {
                const reader = new FileReader()
                reader.onload = (e) => {
                    this.documentUploads[index].preview = e.target.result
                }
                reader.readAsDataURL(file) // Read the file and generate a preview
            }
        },

        async uploadDocument(index) {
            const file = this.documentUploads[index].selectedFile
            const documentName = this.documentUploads[index].name

            if (!file || !documentName) {
                alert('Please enter a document name and select a file before uploading.')
                return
            }

            await this.handlePhotoChange({file}, documentName)  // Pass the file object directly
        },

        onFileUploaded(error, file) {
            if (error) {
                console.error('File upload error:', error)
                return
            }
            console.log('File uploaded:', file)
        },
        submitForm() {
            if (this.validateForm()) {
                this.form.processing = true
                const formData = new FormData()
                // Append main applicant data from `this.form` to `formData`
                Object.keys(this.form).forEach(key => {
                    if (key !== 'children' && key !== 'documents') { // Skip children and documents arrays for separate handling
                        formData.append(key, this.form[key])
                    }
                })
                // Append document files and names
                this.documentUploads.forEach((doc, index) => {
                    if (doc.selectedFile && doc.name) {
                        formData.append(`documents[${index}][file]`, doc.selectedFile)
                        formData.append(`documents[${index}][name]`, doc.name)
                    }
                })
                // Add each child's data
                this.form.childrens.forEach((child, index) => {
                    formData.append(`children[${index}][name]`, child.name)
                    formData.append(`children[${index}][class]`, child.class)
                    formData.append(`children[${index}][school_name]`, child.school_name)
                    if (child.photo) {
                        formData.append(`children[${index}][photo]`, child.photo)
                    }
                })
                console.log('Form Data:', this.form)
                this.form.put(this.route('staff.update', this.applicant.id), {
                    onSuccess: () => this.form.reset(),
                })
            }
        },
        /*    addMoreDocument() {
                // Add a new document upload field to the array
                this.documentUploads.push({
                    selectedFile: null,
                    name: '',
                    uploaded: false,
                    preview: null,
                })
            },*/
        isImage(fileUrl) {
            // Check if the file is an image by its MIME type
            return fileUrl && fileUrl.startsWith('data:image')
        },
        async handlePhotoChange(eventOrFile, fileName) {
            let file

            // Check if the first argument is an event object or a file
            if (eventOrFile instanceof File) {
                // If a file is passed directly (from `uploadDocument` or other places)
                file = eventOrFile
            } else if (eventOrFile && eventOrFile.target && eventOrFile.target.files) {
                // If it's a file input change event
                file = eventOrFile.target.files[0]
            } else if (eventOrFile.file) {
                // If it's a FilePond file event
                file = eventOrFile.file
            }

            if (!file) {
                console.error('No file to upload')
                return
            }

            try {
                const formData = new FormData()
                formData.append('file', file)
                formData.append('name', fileName)
                formData.append('user_id', this.applicant.id)

                const response = await axios.post(this.route('document.add'), formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })

                this.uploadedFiles[fileName] = response.data.filePath
                this.form[fileName] = response.data.filePath
                this.form.documents.push({
                    name: fileName,
                    path: response.data.filePath,
                })

                console.log(this.form.documents)

                console.log(`File uploaded successfully: ${response.data.filePath}`)
            } catch (error) {
                console.error('Error uploading file:', error)
            }
        },

        async removeFile(event, fileName) {
            console.error(event)
            if (!event) {
                console.error('Event is undefined!')
                return
            }


            event.preventDefault()
            console.log('insside remov  ' + fileName)
            const index = this.form.documents.findIndex(doc => doc.name === fileName)
            if (index !== -1) {
                // Add the file to removedFiles
                this.form.removedFiles.push(this.form.documents[index])
                // Remove it from documents array
                this.form.documents.splice(index, 1)
            }
        },
        validateForm() {
            this.form.errors = {} // Reset errors

            if (!this.form.salutation) this.form.errors.salutation = 'Salutation name is required'
            if (!this.form.name) this.form.errors.name = 'Applicant name is required'
            if (!this.form.father_name) this.form.errors.father_name = 'Father name is required'
            if (!this.form.date_of_birth) this.form.errors.date_of_birth = 'Date of birth is required'
            if (!this.form.contact_number) {
                this.form.errors.contact_number = 'Contact number is required'
            } else if (!/^\d{10}$/.test(this.form.contact_number)) {
                this.form.errors.contact_number = 'Contact number must be 10 digits'
            }

            if (this.form.marital_status.length <= 0) {
                this.form.errors.marital_status = 'Marital status is required'
            }
            if (!this.form.current_address) this.form.errors.current_address = 'Current address is required'

            const academicQualificationsComponent = this.$refs.academicQualifications

            // Validate the academic qualifications
            //academicQualificationsComponent.validateRows()
            /*
                        if (!this.form.mother_name) this.form.errors.mother_name = 'Mother name is required'
                        if (!this.form.current_address) this.form.errors.current_address = 'Current address is required'
                        if (!this.form.permanent_address) this.form.errors.permanent_address = 'Current address is required'
                        if (!this.form.schooling_name) this.form.errors.schooling_name = 'Schooling name is required'
                        if (!this.form.schooling_medium) this.form.errors.schooling_medium = 'Schooling medium is required'
                        if (!this.form.favourite_subjects) this.form.errors.favourite_subjects = 'Favourite subjects is required'
                        if (!this.form.teaching_class_limit) this.form.errors.teaching_class_limit = 'Teaching class limit is required'
                        if (!this.form.reason_to_join) this.form.errors.reason_to_join = 'Reason to join is required'
                        if (!this.form.about_academy) this.form.errors.about_academy = 'About academy is required'
                        if (!this.form.prefer_teaching_reason) this.form.errors.prefer_teaching_reason = 'Preferred teaching reason is required'
                        if (!this.form.positive_points_by_trainer) this.form.errors.positive_points_by_trainer = 'Positive points are required'
                        if (!this.form.negative_points_by_trainer) this.form.errors.negative_points_by_trainer = 'Negative points are required'*/
            alert(this.form.errors)
            return Object.keys(this.form.errors).length === 0
        },
    },

}
</script>
